/* eslint-disable */
import React, { useState } from "react";
import {
  Button,
  Card,
  Container,
  Grid,
  Typography,
  Paper,
  Box,
  Chip,
  Tooltip,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import GetAppIcon from "@material-ui/icons/GetApp";
import InfoIcon from "@material-ui/icons/Info";
import DateRangeIcon from "@material-ui/icons/DateRange";
import PaymentIcon from "@material-ui/icons/Payment";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PersonIcon from "@material-ui/icons/Person";
import HomeIcon from "@material-ui/icons/Home";
import TouchAppIcon from "@material-ui/icons/TouchApp";

import exportToCsv from "../../../../../shared/functions/generateCsv";
import AgentReportDialog from "./agent-report-dialog.shared";
import sqlToStandardDate from "../../../../../shared/functions/sqlToStandardDate";
import Loading from "../../../../../Loading";

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(4),
  },
  tileCard: {
    padding: theme.spacing(2),
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    },
  },
  tileLabel: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  tileValue: {
    fontWeight: 600,
    fontSize: "1.8rem",
  },
  orderCard: {
    border: "none",
    borderLeft: "4px solid",
    borderRadius: theme.shape.borderRadius,
    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
    cursor: "pointer",
    "&:hover": {
      transform: "translateX(2px)",
      boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
    },
  },
  orderHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  orderTitle: {
    fontWeight: 600,
  },
  lineItemContainer: {
    marginTop: theme.spacing(1),
  },
  lineItem: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
    "& svg": {
      fontSize: 16,
      marginRight: theme.spacing(0.5),
      color: theme.palette.text.secondary,
    },
  },
  lineItemLabel: {
    fontWeight: 500,
    marginRight: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  lineItemValue: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  downloadButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
  instructionBox: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    "& svg": {
      marginRight: theme.spacing(1),
      color: theme.palette.info.main,
    },
  },
  statusChip: {
    fontWeight: 500,
    marginLeft: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(1, 0),
  },
  noData: {
    textAlign: "center",
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.secondary,
  },
}));

/**
 * A component for displaying a line item with a specified value.
 *
 * @param {object} props - The component props.
 * @param {string} props.value - The value to display in the line item.
 * @param {string} props.label - The label associated with the value.
 * @param {React.ReactNode} props.icon - Icon to display next to the label.
 *
 * @returns {React.ReactElement} A component displaying the label and value with an icon.
 */
function LineItem({ value = "", label = "", icon }) {
  const classes = useStyles();

  return (
    <div className={classes.lineItem}>
      {icon}
      <Typography
        variant="body2"
        component="span"
        className={classes.lineItemLabel}
      >
        {label}:
      </Typography>
      <Typography
        variant="body2"
        component="span"
        className={classes.lineItemValue}
      >
        {value || "Not available"}
      </Typography>
    </div>
  );
}

/**
 * AgentReport is a component that displays a report for agents with order statistics, a table of order data,
 * and the ability to download the report as a CSV file. It also provides a modal to view detailed customer information.
 *
 * @component
 *
 * @param {object} props - The properties passed to the component.
 * @param {Array<object>} props.data - The data for the report, typically an array of order objects.
 * @param {boolean} props.loading - A flag indicating if the report is loading data.
 * @param {Array<{label: string, value: number}>} props.tiles - An array of objects representing summary statistics
 *        to be displayed as tiles, where each object contains a `label` and a `value`.
 *
 * @returns {React.Component} - The rendered component.
 */
function AgentReport({
  data = [],
  loading = false,
  tiles = [],
  impersonation,
}) {
  const classes = useStyles();
  // State to control the visibility of the customer detail modal
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  // State to hold the data of the selected customer for display in the modal
  const [customerData, setCustomerData] = useState([]);

  /**
   * Determines the status of an order based on its lifecycle dates and onboarding status.
   *
   * @param {object} params - The parameters for determining the status.
   * @param {string|null} params.orderPaidDate - The date when the order was paid.
   * @param {string|null} params.activationDate - The date when the order was activated.
   * @param {string|null} params.commissionDate - The date when the commission was paid.
   * @param {string} params.onboardingStatus - The onboarding status of the order.
   *
   * @returns {object} - Returns status object with status code and color.
   */
  const determineStatus = ({
    orderPaidDate,
    activationDate,
    commissionDate,
    onboardingStatus,
  }) => {
    if (onboardingStatus === "Reactivation") {
      return {
        status: 4,
        color: "#ec4d1f",
        label: "Reactivated",
      };
    }

    if (commissionDate) {
      return {
        status: 3,
        color: "#10284b",
        label: "Commission Paid",
      };
    }

    if (activationDate) {
      return {
        status: 2,
        color: "#3bc3a9",
        label: "Activated",
      };
    }

    if (orderPaidDate) {
      return {
        status: 1,
        color: "#ff9a2b",
        label: "Paid",
      };
    }

    return {
      status: 0,
      color: "#e0e0e0",
      label: "Placed",
    };
  };

  // Toggle customer modal
  const toggleCustomerModal = () => {
    setShowCustomerModal(prev => !prev);
  };

  return (
    <Loading loading={loading}>
      <Container className={classes.root}>
        <Grid container spacing={3}>
          {/* Stats Tiles */}
          <Grid item container spacing={2} xs={12}>
            {tiles.map(item => (
              <Grid item xs={6} md={3} key={item.label}>
                <Paper className={classes.tileCard} elevation={1}>
                  <Typography variant="subtitle2" className={classes.tileLabel}>
                    {item.label}
                  </Typography>
                  <Typography variant="h4" className={classes.tileValue}>
                    {item.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Instructions and Download Button */}
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
            >
              <Box className={classes.instructionBox}>
                <TouchAppIcon />
                <Typography variant="body2">
                  Click on any order card to view detailed information
                </Typography>
              </Box>
              <Button
                color="primary"
                variant="contained"
                startIcon={<GetAppIcon />}
                className={classes.downloadButton}
                onClick={() => exportToCsv(data, "Agent Report")}
              >
                Download Report
              </Button>
            </Box>
          </Grid>

          {/* Order Cards */}
          <Grid item xs={12}>
            {data && data.length > 0 ? (
              data.map(row => {
                const lifeCycleStatus = determineStatus({
                  orderPaidDate: row.order_paid_date,
                  activationDate: row.order_activation_date,
                  commissionDate: row.paid_date,
                  onboardingStatus: row.customer_status_2,
                });

                return (
                  <Card
                    key={row.orders_import_id}
                    className={classes.orderCard}
                    style={{ borderLeftColor: lifeCycleStatus.color }}
                    onClick={() => {
                      setCustomerData(row);
                      setShowCustomerModal(true);
                    }}
                  >
                    <div className={classes.orderHeader}>
                      <Typography variant="h6" className={classes.orderTitle}>
                        {row.orders_import_id}
                      </Typography>
                      <Tooltip title={lifeCycleStatus.label}>
                        <Chip
                          size="small"
                          label={lifeCycleStatus.label}
                          style={{
                            backgroundColor: lifeCycleStatus.color,
                            color:
                              lifeCycleStatus.status === 0 ? "#000" : "#fff",
                          }}
                          className={classes.statusChip}
                        />
                      </Tooltip>
                    </div>

                    <Divider className={classes.divider} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box className={classes.lineItemContainer}>
                          <LineItem
                            label="Created"
                            value={sqlToStandardDate(row.order_created_date)}
                            icon={<DateRangeIcon />}
                          />
                          <LineItem
                            label="Paid"
                            value={sqlToStandardDate(row.order_paid_date)}
                            icon={<PaymentIcon />}
                          />
                          <LineItem
                            label="Activated"
                            value={sqlToStandardDate(row.order_activation_date)}
                            icon={<CheckCircleIcon />}
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box className={classes.lineItemContainer}>
                          <LineItem
                            label="Customer"
                            value={row.customer_name}
                            icon={<PersonIcon />}
                          />
                          <LineItem
                            label="Address"
                            value={row.customer_address}
                            icon={<HomeIcon />}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>
                );
              })
            ) : (
              <Paper className={classes.noData} elevation={0}>
                <InfoIcon fontSize="large" color="disabled" />
                <Typography variant="body1">No orders to display</Typography>
              </Paper>
            )}
          </Grid>
        </Grid>

        {/* Customer Detail Modal */}
        <AgentReportDialog
          impersonation={impersonation}
          open={showCustomerModal}
          customerData={customerData}
          toggleDialog={toggleCustomerModal}
        />
      </Container>
    </Loading>
  );
}

export default AgentReport;
